import { useCallback, useMemo, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { Box, FormControl, InputLabel, MenuItem, Select, type SelectChangeEvent } from "@mui/material";
import { RpcTreeForm } from "../RpcTreeForm";
import { UseEndpointProvider } from "../../contexts";
import { useSubmitRpcMutation } from "../../services/algoManagementApi";
import { useAlerts } from "@/hooks";
import { getDefaultRpcEntries } from "../../utils/defaultRpcs";
import { normalizeComponents } from "../../utils";
import { getErrorMessage } from "@/utils/getErrorMessage";
import type { InteractionDataByConnection } from "../../utils/filterInteractionRpcs";

interface DeploymentOption {
  deploymentId: string;
  deploymentName: string;
  sessionId: string;
  algoId: string | undefined;
  rpcName: string;
  interaction: any;
  startSchema: any;
}

interface StrategyOption {
  displayName: string;
  deployments: DeploymentOption[];
}

// White standard-variant select: white label/text/underline, and keep the
// focused underline white instead of the theme's primary (yellow).
const WHITE_SELECT_SX = {
  minWidth: 240,
  "& .MuiInputLabel-root, & .MuiInputLabel-root.Mui-focused": { color: "#fff" },
  "& .MuiInputBase-root": { color: "#fff" },
  "& .MuiSelect-icon": { color: "#fff" },
  "& .MuiInput-root:before": { borderBottomColor: "rgba(255,255,255,0.7)" },
  "& .MuiInput-root:hover:not(.Mui-disabled):before": { borderBottomColor: "#fff" },
  "& .MuiInput-root:after": { borderBottom: "2px solid #fff" },
} as const;

export interface NewStrategyDetailProps {
  interactionsData?: InteractionDataByConnection;
  onSubmitted?: () => void;
}

export const NewStrategyDetail = ({ interactionsData, onSubmitted }: NewStrategyDetailProps) => {
  const { showError, showSuccess } = useAlerts();
  const [submitRpc] = useSubmitRpcMutation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedStrategy, setSelectedStrategy] = useState<string>("");
  const [selectedDeploymentId, setSelectedDeploymentId] = useState<string>("");

  // Group each session's `start` RPC by its display_name (the Strategy), then by
  // deployment; the first session seen per deployment is the one we start against.
  const strategies = useMemo<StrategyOption[]>(() => {
    const byStrategy = new Map<string, Map<string, DeploymentOption>>();
    for (const [, interaction] of Object.entries(interactionsData ?? {})) {
      for (const entry of getDefaultRpcEntries(interaction)) {
        if (entry.slot !== "start") continue;
        const ui = (entry.schema as any)?.ui as { display_name?: string } | undefined;
        const displayName =
          (typeof ui?.display_name === "string" && ui.display_name) ||
          (typeof (entry.schema as any)?.title === "string" && (entry.schema as any).title) ||
          entry.rpcName;

        const deploymentId = String((interaction as any)?.deployment_id ?? "");
        const deployments = byStrategy.get(displayName) ?? new Map<string, DeploymentOption>();
        if (!deployments.has(deploymentId)) {
          deployments.set(deploymentId, {
            deploymentId,
            deploymentName: String((interaction as any)?.deployment_name ?? deploymentId),
            sessionId: String((interaction as any)?.session_id ?? ""),
            algoId: (interaction as any)?.algo_id,
            rpcName: entry.rpcName,
            interaction,
            startSchema: entry.schema,
          });
        }
        byStrategy.set(displayName, deployments);
      }
    }
    return [...byStrategy.entries()].map(([displayName, deployments]) => ({
      displayName,
      deployments: [...deployments.values()],
    }));
  }, [interactionsData]);

  const activeStrategy = useMemo(
    () => strategies.find((s) => s.displayName === selectedStrategy),
    [strategies, selectedStrategy]
  );
  const activeDeployment = useMemo(
    () => activeStrategy?.deployments.find((d) => d.deploymentId === selectedDeploymentId),
    [activeStrategy, selectedDeploymentId]
  );

  const onStrategyChange = useCallback((e: SelectChangeEvent) => {
    setSelectedStrategy(e.target.value);
    setSelectedDeploymentId("");
  }, []);
  const onDeploymentChange = useCallback((e: SelectChangeEvent) => {
    setSelectedDeploymentId(e.target.value);
  }, []);

  const schemaWithComponents = useMemo(() => {
    if (!activeDeployment) return null;
    return {
      ...activeDeployment.startSchema,
      components: normalizeComponents(activeDeployment.interaction),
    };
  }, [activeDeployment]);

  const handleSubmit = useCallback(
    async (args: any) => {
      if (!activeDeployment) {
        showError("Pick a strategy and deployment before submitting.");
        return;
      }
      setIsSubmitting(true);
      try {
        await submitRpc({
          data: {
            req_id: uuidv4(),
            connection_id: activeDeployment.sessionId,
            algo_id: activeDeployment.algoId,
            rpc_name: activeDeployment.rpcName,
            args,
          },
        }).unwrap();
        showSuccess(`Strategy started`);
        onSubmitted?.();
      } catch (error: any) {
        showError(`Failed to start strategy: ${getErrorMessage(error)}`);
      } finally {
        setIsSubmitting(false);
      }
    },
    [activeDeployment, submitRpc, showError, showSuccess, onSubmitted]
  );

  if (!interactionsData) {
    return <Box sx={{ p: 2, color: "#888" }}>Loading available strategies…</Box>;
  }
  if (strategies.length === 0) {
    return (
      <Box sx={{ p: 2, color: "#888" }}>
        No start RPCs available in the interaction catalog.
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "transparent",
        borderLeft: "3px solid #1c2e57",
      }}
    >
      <Box sx={{ display: "flex", gap: 2, p: 2 }}>
        <FormControl variant="standard" size="small" sx={WHITE_SELECT_SX}>
          <InputLabel id="new-strategy-strategy-label">Strategy</InputLabel>
          <Select
            labelId="new-strategy-strategy-label"
            value={selectedStrategy}
            onChange={onStrategyChange}
          >
            {strategies.map((s) => (
              <MenuItem key={s.displayName} value={s.displayName}>
                {s.displayName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl variant="standard" size="small" sx={WHITE_SELECT_SX} disabled={!activeStrategy}>
          <InputLabel id="new-strategy-deployment-label">Deployment</InputLabel>
          <Select
            labelId="new-strategy-deployment-label"
            value={selectedDeploymentId}
            onChange={onDeploymentChange}
          >
            {(activeStrategy?.deployments ?? []).map((d) => (
              <MenuItem key={d.deploymentId} value={d.deploymentId}>
                {d.deploymentName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {activeDeployment && schemaWithComponents ? (
        <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
          <UseEndpointProvider
            interaction={activeDeployment.interaction}
            submitRpc={submitRpc}
            showError={showError}
          >
            <RpcTreeForm
              key={activeDeployment.sessionId}
              schema={schemaWithComponents}
              initialData={{}}
              mode="edit"
              populateDefaults={false}
              isSubmitting={isSubmitting}
              onSubmit={handleSubmit}
              hideHeaders
            />
          </UseEndpointProvider>
        </Box>
      ) : (
        <Box sx={{ p: 2, color: "#888" }}>
          {activeStrategy
            ? "Select a deployment to configure the strategy."
            : "Select a strategy to begin."}
        </Box>
      )}
    </Box>
  );
};
